import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Section, FadeIn } from "@/components/Section";
import { Slider } from "@/components/ui/slider";
import { Calculator, Info, ArrowRight, RefreshCcw } from "lucide-react";

const USD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

const NUM = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

const DEFAULTS = {
  conversations: 100,
  conversionPct: 15,
  price: 299,
  vendorCost: 150,
  platformCost: 499,
};

const RANGES = {
  conversations: { min: 10, max: 1000, step: 5 },
  conversionPct: { min: 1, max: 50, step: 1 },
  price: { min: 50, max: 2500, step: 5 },
  vendorCost: { min: 0, max: 2000, step: 5 },
  platformCost: { min: 0, max: 5000, step: 1 },
};

export default function RoiCalculator({ embedded = false, onStateChange }) {
  const [v, setV] = useState(DEFAULTS);

  const set = (k) => (val) => {
    const r = RANGES[k];
    const clamped = Math.max(r.min, Math.min(r.max, Number(val) || 0));
    setV((prev) => ({ ...prev, [k]: clamped }));
  };

  const reset = () => setV(DEFAULTS);

  const calc = useMemo(() => {
    const purchases = v.conversations * (v.conversionPct / 100);
    const grossRevenue = purchases * v.price;
    const vendorCosts = purchases * v.vendorCost;
    const netBefore = grossRevenue - vendorCosts - v.platformCost;
    const annualized = netBefore * 12;
    return { purchases, grossRevenue, vendorCosts, netBefore, annualized };
  }, [v]);

  // Emit current state up to the parent (admin demo wrapper) so it can
  // "Save scenario to prospect" without duplicating calculator logic.
  useEffect(() => {
    if (onStateChange) onStateChange({ inputs: v, outputs: calc });
  }, [v, calc, onStateChange]);

  return (
    <Section testId="roi-calculator-section" className="bg-white">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">
          Illustrative planning tool
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
          Model your clinic's cash-pay revenue opportunity.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
          Use this simple planning tool to explore how clinic-selected pricing, patient volume, vendor costs, and conversion assumptions may impact potential cash-pay service line economics.
        </p>
      </div>

      <FadeIn>
        <div className="mt-12 rounded-3xl border border-slate-200 bg-slate-50/60 p-5 sm:p-8 lg:p-10 overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Inputs */}
            <div className="lg:col-span-7">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 text-slate-700">
                  <span className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                    <Calculator className="w-4 h-4" />
                  </span>
                  <div className="font-display font-semibold text-slate-900">Your assumptions</div>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="roi-reset-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900"
                >
                  <RefreshCcw className="w-3.5 h-3.5" /> Reset
                </button>
              </div>

              <div className="mt-6 space-y-7">
                <InputRow
                  label="Estimated eligible patient conversations per month"
                  testId="roi-conversations"
                  value={v.conversations}
                  range={RANGES.conversations}
                  onChange={set("conversations")}
                  format={(n) => NUM(n)}
                />
                <InputRow
                  label="Estimated patient conversion rate"
                  testId="roi-conversion"
                  value={v.conversionPct}
                  range={RANGES.conversionPct}
                  onChange={set("conversionPct")}
                  suffix="%"
                  format={(n) => `${n}%`}
                />
                <InputRow
                  label="Average patient cash-pay price"
                  testId="roi-price"
                  value={v.price}
                  range={RANGES.price}
                  onChange={set("price")}
                  prefix="$"
                  format={USD}
                />
                <InputRow
                  label="Estimated Nodera contracted vendor cost per service"
                  testId="roi-vendor"
                  value={v.vendorCost}
                  range={RANGES.vendorCost}
                  onChange={set("vendorCost")}
                  prefix="$"
                  format={USD}
                />
                <InputRow
                  label="Estimated monthly platform / software cost"
                  testId="roi-platform"
                  value={v.platformCost}
                  range={RANGES.platformCost}
                  onChange={set("platformCost")}
                  prefix="$"
                  format={USD}
                />
              </div>
            </div>

            {/* Outputs */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-slate-900 text-white p-6 lg:p-7">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
                  Illustrative outputs
                </div>
                <h3 className="mt-2 font-display text-xl font-semibold leading-snug">
                  Estimated monthly model
                </h3>

                <div className="mt-6 space-y-4">
                  <Metric
                    label="Estimated monthly purchases"
                    value={NUM(calc.purchases)}
                    testId="roi-out-purchases"
                  />
                  <Metric
                    label="Estimated gross revenue"
                    value={USD(calc.grossRevenue)}
                    testId="roi-out-gross"
                  />
                  <Metric
                    label="Estimated contracted vendor costs"
                    value={`− ${USD(calc.vendorCosts)}`}
                    muted
                    testId="roi-out-vendor"
                  />
                  <Metric
                    label="Estimated platform / software cost"
                    value={`− ${USD(v.platformCost)}`}
                    muted
                    testId="roi-out-platform"
                  />

                  <div className="border-t border-white/10 pt-4 space-y-1.5">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
                      Estimated net contribution before other clinic expenses
                    </div>
                    <motion.div
                      key={calc.netBefore}
                      initial={{ opacity: 0.5, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      data-testid="roi-out-net"
                      className="font-display text-3xl font-extrabold tracking-tighter tabular"
                    >
                      {USD(calc.netBefore)}
                      <span className="text-sm font-medium text-slate-400 ml-1.5">/ mo</span>
                    </motion.div>
                  </div>

                  <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-300">
                      Estimated annualized net contribution
                    </div>
                    <motion.div
                      key={calc.annualized}
                      initial={{ opacity: 0.5, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      data-testid="roi-out-annualized"
                      className="mt-1 font-display text-2xl font-bold tabular"
                    >
                      {USD(calc.annualized)}
                      <span className="text-xs font-medium text-slate-400 ml-1.5">/ yr</span>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Compliance disclaimer (directly under outputs) */}
              <div
                data-testid="roi-disclaimer"
                className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 flex gap-3"
              >
                <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                <p className="text-[12.5px] leading-relaxed text-slate-600">
                  This calculator is for informational and illustrative business planning purposes only. It does not guarantee revenue, profit, margin, patient demand, conversion rates, clinical outcomes, or financial performance. Actual results may vary based on clinic operations, patient demand, pricing decisions, vendor agreements, staffing, local market factors, legal requirements, and implementation quality. Clinics are responsible for determining their own pricing, compliance obligations, vendor relationships, patient communications, and service delivery requirements.
                </p>
              </div>

              {/* CTAs — hidden in embedded admin demo mode (target routes are gated) */}
              {!embedded && (
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to={{
                      pathname: "/demo",
                      search: new URLSearchParams({
                        conversations: String(v.conversations),
                        conversion: String(v.conversionPct),
                        price: String(v.price),
                        vendor_cost: String(v.vendorCost),
                        platform_cost: String(v.platformCost),
                        net_monthly: String(Math.round(calc.netBefore)),
                        net_annualized: String(Math.round(calc.annualized)),
                      }).toString(),
                    }}
                    data-testid="roi-build-model-btn"
                    className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-800 transition-colors"
                  >
                    Build My Clinic Opportunity Model <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/programs"
                    data-testid="roi-example-lines-btn"
                    className="inline-flex items-center gap-1.5 bg-white text-slate-900 border border-slate-200 rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-50 transition-colors"
                  >
                    See Example Service Lines
                  </Link>
                </div>
              )}

              {/* Platform-role note */}
              <p className="mt-4 text-[11.5px] leading-relaxed text-slate-500">
                Nodera Health is a software and business enablement platform. Clinical services, vendor services, and patient-facing decisions are managed by the participating clinic and applicable third-party vendors.
              </p>
            </div>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}

function InputRow({ label, value, range, onChange, prefix, suffix, format, testId }) {
  return (
    <div data-testid={`${testId}-row`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <label className="text-sm font-medium text-slate-700 leading-snug">{label}</label>
        <div className="relative shrink-0">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
              {prefix}
            </span>
          )}
          <input
            type="number"
            inputMode="numeric"
            data-testid={`${testId}-input`}
            value={value}
            min={range.min}
            max={range.max}
            step={range.step}
            onChange={(e) => onChange(e.target.value)}
            className={`w-28 sm:w-32 bg-white border border-slate-200 rounded-lg ${prefix ? "pl-6" : "pl-3"} ${suffix ? "pr-6" : "pr-3"} py-2 text-sm font-semibold text-slate-900 text-right tabular focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400`}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">
              {suffix}
            </span>
          )}
        </div>
      </div>
      <Slider
        value={[value]}
        min={range.min}
        max={range.max}
        step={range.step}
        onValueChange={(arr) => onChange(arr[0])}
        data-testid={`${testId}-slider`}
        className="mt-1"
      />
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-400 tabular">
        <span>{format(range.min)}</span>
        <span>{format(range.max)}</span>
      </div>
    </div>
  );
}

function Metric({ label, value, muted, testId }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className={`text-xs ${muted ? "text-slate-400" : "text-slate-300"}`}>{label}</div>
      <div
        data-testid={testId}
        className={`font-display font-semibold tabular ${muted ? "text-slate-300" : "text-white"}`}
      >
        {value}
      </div>
    </div>
  );
}
